import { FC, PropsWithChildren, ReactNode, useCallback, useEffect, useState } from 'react';

import * as Dialog from '@radix-ui/react-dialog';

import {
  contentClass,
  headerClass,
  mainClass,
  menuActiveClass,
  menuBaseClass,
  menuInactiveClass,
  sharedPlainButtonClass,
  styledBottomClass,
} from './MobileHeader.css';
import { CloseIcon, HamburgerMenuIcon, ProfileIcon } from '../../icons';
import { useI18nTranslations } from '../../providers/i18n';
import { getClassNames } from '../../util/css';
import { Box } from '../Box/Box';
import { Divider } from '../Divider/Divider';
import { Stack } from '../Stack/Stack';
import { Text } from '../Text/Text';

type CompoundMobileHeaderProps = FC<{
  /** Controlled state of the Profile Menu */
  isProfileMenuOpen: boolean;
  /** External setter that sets setProfileMenuOpen */
  setProfileMenuOpen: (open: boolean) => void;
  /** Controlled state of the Hamburger Menu */
  isHamburgerMenuOpen: boolean;
  /** External setter that sets isHamburgerMenuOpen */
  setHamburgerMenuOpen: (open: boolean) => void;
  /** Content that will be shown inside the Hamburger menu */
  children: ReactNode;
  /** Logo that links back to the homepage, which should have an accessible description. */
  logoLink: ReactNode;
  isLoggedIn?: boolean;
  profileLink?: {
    href: string;
    text: string;
  };
  hamburgerMenuLabel?: string;
  profileMenuLabel?: string;
  languageSelector?: ReactNode;
  languageSelectorOutsideMenu?: boolean;
}> & {
  HamburgerMenu: typeof HamburgerMenu;
  Top: typeof Top;
  Main: typeof Main;
  Bottom: typeof Bottom;
};

export const MobileHeader: CompoundMobileHeaderProps = ({
  children,
  isProfileMenuOpen,
  setProfileMenuOpen,
  isHamburgerMenuOpen,
  setHamburgerMenuOpen,
  logoLink,
  isLoggedIn,
  profileLink,
  hamburgerMenuLabel,
  profileMenuLabel,
  languageSelector,
  languageSelectorOutsideMenu,
}) => {
  const [isAnyMenuOpen, setIsAnyMenuOpen] = useState(isHamburgerMenuOpen || isProfileMenuOpen);
  const { languageNavigation } = useI18nTranslations();

  const onToggleHamburgerMenu = () => {
    setHamburgerMenuOpen(!isHamburgerMenuOpen);
  };

  const onToggleProfileMenu = () => {
    setProfileMenuOpen(!isProfileMenuOpen);
  };

  useEffect(() => {
    if (!isProfileMenuOpen && !isHamburgerMenuOpen && isAnyMenuOpen) {
      const timeoutId = window.setTimeout(() => {
        setIsAnyMenuOpen(false);
      }, 300);

      return () => window.clearTimeout(timeoutId);
    }

    if (isHamburgerMenuOpen || isProfileMenuOpen) {
      setIsAnyMenuOpen(true);
    }
  }, [isHamburgerMenuOpen, isProfileMenuOpen, isAnyMenuOpen]);

  const ProfileButtonContent = () => (
    <Stack alignX="center">
      <ProfileIcon />
      {profileMenuLabel && (
        <Text weight="bold" size="BodyXS">
          {profileMenuLabel}
        </Text>
      )}
    </Stack>
  );

  const HamburgerMenuContent = () => (
    <Stack alignX="center">
      {isHamburgerMenuOpen ? <CloseIcon /> : <HamburgerMenuIcon />}
      {hamburgerMenuLabel && (
        <Text weight="bold" size="BodyXS">
          {hamburgerMenuLabel}
        </Text>
      )}
    </Stack>
  );

  return (
    <Dialog.Root open={isAnyMenuOpen}>
      <Stack className={headerClass} alignX="justify" alignY="center" direction="row">
        <Dialog.Trigger asChild>
          <button type="button" className={sharedPlainButtonClass} onClick={onToggleHamburgerMenu}>
            <HamburgerMenuContent />
          </button>
        </Dialog.Trigger>

        {logoLink}

        <Stack direction="row" alignY="center">
          {languageSelector && (
            <nav aria-label={languageNavigation}>
              <Stack direction="row" gap="5" alignX="end" alignY="center">
                {languageSelector}
              </Stack>
            </nav>
          )}

          {isLoggedIn || !profileLink ? (
            <Dialog.Trigger asChild>
              <button type="button" className={sharedPlainButtonClass} onClick={onToggleProfileMenu}>
                <ProfileButtonContent />
              </button>
            </Dialog.Trigger>
          ) : (
            <a className={sharedPlainButtonClass} href={profileLink.href}>
              <ProfileButtonContent />
            </a>
          )}
        </Stack>
      </Stack>

      <Divider />

      <Dialog.Portal>
        <Dialog.Overlay>
          <Dialog.Content className={contentClass}>
            {!isProfileMenuOpen && (
              <>
                <Stack className={headerClass} alignX="justify" alignY="center" direction="row">
                  <Dialog.Trigger asChild>
                    <button type="button" className={sharedPlainButtonClass} onClick={onToggleHamburgerMenu}>
                      <HamburgerMenuContent />
                    </button>
                  </Dialog.Trigger>

                  {logoLink}

                  {profileLink && (
                    <Stack direction="row" alignY="center">
                      {languageSelectorOutsideMenu && languageSelector && languageSelector}

                      <Dialog.Trigger asChild>
                        <button type="button" className={sharedPlainButtonClass} onClick={onToggleProfileMenu}>
                          <ProfileButtonContent />
                        </button>
                      </Dialog.Trigger>
                    </Stack>
                  )}
                </Stack>
                <Divider />
              </>
            )}

            {children}
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

type HamburgerProps = {
  children: ReactNode;
  isOpen: boolean;
};

const HamburgerMenu: FC<PropsWithChildren<HamburgerProps>> = ({ children, isOpen }) => {
  const [isVisible, setIsVisible] = useState(isOpen);

  useEffect(() => {
    if (isOpen && !isVisible) {
      setIsVisible(true);
    }

    if (isOpen) {
      window.scrollTo(0, 0);
    }
  }, [isOpen, isVisible]);

  const onAnimationEnd = useCallback(() => {
    if (!isOpen) {
      setIsVisible(false);
    }
  }, [isOpen]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      data-cy="dropMenu"
      className={getClassNames(menuBaseClass, {
        [menuActiveClass]: isOpen,
        [menuInactiveClass]: !isOpen,
      })}
      onAnimationEnd={onAnimationEnd}>
      {children}
    </div>
  );
};

type TopsiteProps = {
  children: ReactNode;
};

const Top: FC<PropsWithChildren<TopsiteProps>> = ({ children }) => {
  const { domainNavigation } = useI18nTranslations();

  return (
    <Box paddingX="6" paddingY="4">
      <nav aria-label={domainNavigation}>
        <Stack direction="row" gap="4" as="ul">
          {children}
        </Stack>
      </nav>
    </Box>
  );
};

type MainProps = {
  children: ReactNode;
};

const Main: FC<PropsWithChildren<MainProps>> = ({ children }) => {
  return (
    <Box className={mainClass} paddingX="10" paddingY="3">
      {children}
    </Box>
  );
};

type BottomProps = {
  children: ReactNode;
};

const Bottom: FC<PropsWithChildren<BottomProps>> = ({ children }) => (
  <>
    <Divider />
    <Stack className={styledBottomClass} direction="row" alignX="end">
      {children}
    </Stack>
  </>
);

MobileHeader.Bottom = Bottom;
MobileHeader.HamburgerMenu = HamburgerMenu;
MobileHeader.Main = Main;
MobileHeader.Top = Top;

MobileHeader.Bottom.displayName = 'MobileHeader.Bottom';
MobileHeader.HamburgerMenu.displayName = 'MobileHeader.HamburgerMenu';
MobileHeader.Main.displayName = 'MobileHeader.Main';
MobileHeader.Top.displayName = 'MobileHeader.Top';
